/**
 * English: Chat org picker — lists Limbic active orgs and exchanges Limbic JWT.
 * Thai: ตัวเลือก org สำหรับแชท — รายการ org ที่ Limbic active แล้วแลก JWT
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ENDPOINTS } from '../../config/api';
import { useAuth } from '../../context/AuthContext';
import Button from '../shared/Button';
import LoadingSpinner from '../shared/LoadingSpinner';

type EnabledOrg = { org_name: string; display_name: string };

interface OrgPickerProps {
  onSelected?: (orgName: string) => void;
}

export default function OrgPicker({ onSelected }: OrgPickerProps) {
  const { t } = useTranslation();
  const { selectOrg, selectedOrgName, token, logout } = useAuth();
  const navigate = useNavigate();
  const [orgs, setOrgs] = useState<EnabledOrg[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [picked, setPicked] = useState(selectedOrgName || '');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const headers: Record<string, string> = { Accept: 'application/json' };
        if (token) headers.Authorization = `Bearer ${token}`;
        const res = await fetch(ENDPOINTS.adminOrganizations, {
          credentials: 'include',
          headers,
        });
        const data = (await res.json().catch(() => ({}))) as {
          organizations?: EnabledOrg[];
          error?: string;
        };
        if (cancelled) return;
        if (!res.ok) {
          setError(data.error || res.statusText || t('chat.orgPickerLoadError'));
          setOrgs([]);
          return;
        }
        setOrgs(data.organizations || []);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : t('chat.orgPickerLoadError'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, t]);

  const confirm = async () => {
    if (!picked.trim()) {
      setError(t('chat.orgPickerRequired'));
      return;
    }
    setBusy(true);
    setError('');
    const result = await selectOrg(picked.trim());
    setBusy(false);
    if (!result.ok) {
      if (result.reauth) {
        // Clear local session first so LoginRoute does not bounce authenticated users to /
        logout();
        navigate('/login?returnTo=/chat-test', { replace: true });
        return;
      }
      setError(result.error);
      return;
    }
    onSelected?.(picked.trim());
  };

  return (
    <div className="max-w-md mx-auto mt-16 px-4">
      <h2 className="text-xl font-semibold text-app-text mb-2">{t('chat.orgPickerTitle')}</h2>
      <p className="text-sm text-app-text-sec mb-6">{t('chat.orgPickerSubtitle')}</p>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 dark:bg-red-900/30 px-4 py-2.5 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-8">
          <LoadingSpinner />
        </div>
      ) : (
        <div className="space-y-4">
          <label className="block text-xs font-medium text-app-text-sec mb-1">
            {t('chat.orgPickerLabel')}
          </label>
          <select
            className="w-full bg-app-bg border border-app-border rounded-lg px-3 py-2.5 text-sm text-app-text outline-none focus:ring-1 focus:ring-app-accent"
            value={picked}
            onChange={(e) => setPicked(e.target.value)}
            disabled={busy || orgs.length === 0}
          >
            <option value="">{t('chat.orgPickerPlaceholder')}</option>
            {orgs.map((o) => (
              <option key={o.org_name} value={o.org_name}>
                {o.display_name || o.org_name}
              </option>
            ))}
          </select>

          {orgs.length === 0 && (
            <p className="text-sm text-app-text-sec">
              {t('chat.orgPickerEmpty')}{' '}
              <Link to="/organizations" className="text-app-accent underline">
                {t('chat.orgPickerGoOrganizations')}
              </Link>
            </p>
          )}

          <Button type="button" className="w-full" disabled={busy || !picked} onClick={() => void confirm()}>
            {busy ? <LoadingSpinner size="sm" /> : t('chat.orgPickerContinue')}
          </Button>
        </div>
      )}
    </div>
  );
}
